/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { jsx, cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box, Text } from '@atlaskit/primitives/compiled';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';

import { ContentTreeSearchBase } from '@confluence/content-tree-search/entry-points/ContentTreeSearchBase.ts';

const styles = cssMap({
	movePopupContent: {
		padding: token('space.150'),
		width: '280px',
	},
	searchHint: {
		padding: token('space.100'),
		color: token('color.text.subtlest'),
	},
	moveButtonOpen: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
	},
});

const i18n = defineMessages({
	moveButton: {
		id: 'side-navigation.review-content-suggestions-modal.move-button',
		defaultMessage: 'Move',
		description: 'Label for the move button on a suggestion item row',
	},
	searchHint: {
		id: 'side-navigation.review-content-suggestions-modal.move-search-hint',
		defaultMessage: 'Search',
		description: 'Hint text shown in the move popup before the user has typed a search query',
	},
});

export type SuggestionMovePopupProps = {
	suggestionId: string;
	spaceKey?: string;
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	onMoveSuggestion: (suggestionId: string, newParentId: number) => void;
};

export const SuggestionMovePopup = ({
	suggestionId,
	spaceKey,
	isOpen,
	onOpenChange,
	onMoveSuggestion,
}: SuggestionMovePopupProps) => {
	const { formatMessage } = useIntl();

	const handleMoveItemClick = (idOrAri: string) => {
		const rawId = idOrAri.includes('/') ? (idOrAri.split('/').pop() ?? idOrAri) : idOrAri;
		const numId = Number(rawId);
		if (!rawId || isNaN(numId)) return;
		onMoveSuggestion(suggestionId, numId);
		onOpenChange(false);
	};

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => onOpenChange(false)}
			placement="bottom-start"
			zIndex={800}
			content={() => (
				<Box xcss={styles.movePopupContent}>
					<ContentTreeSearchBase
						spaceKey={spaceKey ?? ''}
						onItemClick={handleMoveItemClick}
						contentId=""
					>
						<Box xcss={styles.searchHint}>
							<Text color="color.text.subtlest">{formatMessage(i18n.searchHint)}</Text>
						</Box>
					</ContentTreeSearchBase>
				</Box>
			)}
			trigger={(triggerProps) => (
				<Box xcss={cx(isOpen && styles.moveButtonOpen)}>
					<Button
						{...triggerProps}
						appearance="subtle"
						spacing="compact"
						onClick={() => onOpenChange(!isOpen)}
						testId={`review-content-suggestion-move-button-${suggestionId}`}
					>
						{formatMessage(i18n.moveButton)}
					</Button>
				</Box>
			)}
		/>
	);
};
